import { useEffect, useRef, useState } from 'react';
import { api } from '../utils/api.js';
import { friendlyError } from '../utils/errors.js';

// Tech Wiki / SOP assistant — company-process Q&A grounded in the Notion wiki.
//
// Simpler than Troubleshoot: no sessions, no photos, no diagnosis card. The
// conversation lives in component state (a short rolling history is sent to the
// worker for follow-ups; nothing is persisted long-term in v1). The worker
// classifies each answer (grounded / redirect / decline / no-answer) and we
// style the reply accordingly.

// Curated fallback / cold-start suggestions — all verified answerable from the
// current Notion wiki. Trending (most-asked grounded) questions from the API are
// blended in FRONT of these once there's question history (see `chips` below),
// so the chips become data-driven as the team uses the wiki.
const CURATED = [
  'How do I accept a credit card payment?',
  'What info do I need for an ACH payment?',
  'How do I add a second tech to a job?',
  'How do I make outgoing calls to customers?',
];

let msgSeq = 0;
const nextId = () => `m-${++msgSeq}`;

export default function Wiki() {
  const [messages, setMessages] = useState([]); // [{ id, role:'tech'|'assistant', content, cited?, signal? }]
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [trending, setTrending] = useState([]); // most-asked grounded questions

  const transcriptRef = useRef(null);
  const inputRef = useRef(null);
  const stickToBottomRef = useRef(true);

  function handleTranscriptScroll(e) {
    const el = e.currentTarget;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }

  // Auto-scroll to the newest message unless the tech scrolled up to re-read.
  useEffect(() => {
    const el = transcriptRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages.length, busy]);

  useEffect(() => {
    if (!busy && inputRef.current) inputRef.current.focus({ preventScroll: true });
  }, [busy]);

  // Fetch trending (most-asked grounded) questions once. Silent fallback to the
  // curated list on any error / empty result.
  useEffect(() => {
    let cancelled = false;
    api.wikiTrending()
      .then((d) => { if (!cancelled) setTrending(Array.isArray(d.questions) ? d.questions : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  async function ask(overrideText) {
    if (busy) return;
    const question = (overrideText ?? input).trim();
    if (!question) return;

    setBusy(true);
    setError(null);

    // Rolling history = everything so far (worker trims to the last few turns).
    const history = messages.map((m) => ({ role: m.role, content: m.content }));

    const techMsg = { id: nextId(), role: 'tech', content: question };
    setMessages((prev) => [...prev, techMsg]);
    setInput('');

    try {
      const data = await api.askWiki({ question, history });
      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'assistant',
          content: data.answer || "I couldn't find that in the wiki.",
          cited: Array.isArray(data.cited) ? data.cited : [],
          signal: data.signal || null,
          followups: Array.isArray(data.followups) ? data.followups : [],
        },
      ]);
    } catch (e) {
      // Roll back the optimistic question so the tech can retry cleanly, and
      // restore it in the box (only from the typed path, not a suggestion tap).
      setMessages((prev) => prev.filter((m) => m.id !== techMsg.id));
      if (overrideText == null) setInput(question);
      setError(friendlyError(e, 'ask the wiki'));
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      ask();
    }
  }

  const started = messages.length > 0;
  // Follow-up chips show only under the LATEST answer (the worker returns
  // followups only on grounded answers, so redirects/declines have none).
  const lastMsg = messages[messages.length - 1];
  const followups = !busy && lastMsg?.role === 'assistant' ? (lastMsg.followups || []) : [];

  // Suggestion chips: trending (most-asked) first, then curated, deduped, max 4.
  const chips = (() => {
    const seen = new Set();
    const out = [];
    for (const q of [...trending, ...CURATED]) {
      const key = q.trim().toLowerCase();
      if (!q.trim() || seen.has(key)) continue;
      seen.add(key);
      out.push(q);
      if (out.length >= 4) break;
    }
    return out;
  })();

  return (
    <div className="px-5 py-6 max-w-3xl mx-auto space-y-4 pb-24">
      {/* Header */}
      <div>
        <h1 className="h-display text-2xl text-white mb-1">Tech Wiki / SOP</h1>
        <p className="text-ink-400 text-sm">
          Company policies, SOPs, distributors, warranty &amp; coding — answered from our tech wiki.
        </p>
      </div>

      {/* Intro + example questions, only before the first message */}
      {!started && (
        <div className="card">
          <p className="text-ink-200 font-semibold mb-1">Ask a company-process question</p>
          <p className="text-ink-400 text-sm mb-4">
            Repair questions? Head to the Troubleshoot tab — this covers how we run jobs, not how to fix a unit.
          </p>
          <div className="flex flex-wrap gap-2">
            {chips.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => ask(q)}
                disabled={busy}
                className="inline-flex items-center justify-center min-h-[48px] px-3 py-2 bg-ink-800 hover:bg-ink-700 text-ink-200 rounded-lg text-sm text-left border border-ink-700 active:scale-[0.97] transition disabled:opacity-50"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Transcript */}
      {started && (
        <div
          ref={transcriptRef}
          onScroll={handleTranscriptScroll}
          className="space-y-3 max-h-[62vh] overflow-y-auto pr-1"
        >
          {messages.map((m) => (
            <Message key={m.id} message={m} />
          ))}
          {busy && <Thinking />}
        </div>
      )}

      {/* Suggested follow-ups under the latest answer — tap to ask. */}
      {followups.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="w-full text-ink-400 text-xs">You could also ask:</span>
          {followups.map((q, i) => (
            <button
              key={i}
              type="button"
              onClick={() => ask(q)}
              className="inline-flex items-center min-h-[44px] px-3 py-2 bg-ink-800 hover:bg-ink-700 text-ink-200 rounded-lg text-sm text-left border border-ink-700 active:scale-[0.97] transition"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="card border-fail/30 bg-fail/5">
          <p className="text-fail-400">⚠ {error}</p>
        </div>
      )}

      {/* Composer — inline in the flow (not fixed), so iOS Safari scrolls the
          focused textarea above the on-screen keyboard on its own. The
          transcript above is its own capped scroll area, so this stays put. */}
      <div className="card !p-3 bg-ink-900 border-ink-700">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask about a policy, distributor, warranty step, job code…"
          rows={2}
          disabled={busy}
          className="w-full bg-transparent text-white placeholder-ink-500 focus:outline-none resize-none disabled:opacity-50"
        />
        <div className="flex items-center justify-end mt-2">
          <button
            onClick={() => ask()}
            disabled={busy || !input.trim()}
            className="btn-primary"
          >
            {busy ? 'Asking…' : 'Ask →'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function Message({ message }) {
  const isTech = message.role === 'tech';
  if (isTech) {
    return (
      <div className="rounded-2xl px-4 py-3 bg-mra-red/10 border border-mra-red/30 text-ink-100 ml-8">
        <span className="badge bg-mra-red/15 text-mra-red border border-mra-red/30 mb-1 inline-block">You</span>
        <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
      </div>
    );
  }

  // Assistant reply. Redirect/decline read as gentle info, not an answer.
  const isGrounded = message.signal === 'GROUNDED';
  const tone =
    message.signal === 'REDIRECT' || message.signal === 'DECLINE'
      ? 'bg-marginal/5 border border-marginal/30 text-ink-100'
      : 'bg-ink-900 border border-ink-700 text-ink-100';

  return (
    <div className={`rounded-2xl px-4 py-3 ${tone}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="badge bg-ink-800 text-ink-300 border border-ink-700">Wiki</span>
        {isGrounded && <span className="badge-source">Grounded</span>}
        {message.signal === 'NO_WIKI_ANSWER' && (
          <span className="badge bg-marginal/15 text-marginal border border-marginal/30">Flagged for the team</span>
        )}
      </div>

      <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>

      {/* Citations — the wiki page › heading each answer drew from. */}
      {Array.isArray(message.cited) && message.cited.length > 0 && (
        <div className="mt-3 pt-3 border-t border-ink-800 space-y-1">
          {message.cited.map((c, i) => (
            <div key={i} className="text-ink-400 text-xs">
              <span className="text-ink-400">from:</span>{' '}
              {[c.page_title, c.heading].filter(Boolean).join(' › ') || 'the wiki'}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Thinking() {
  return (
    <div className="rounded-2xl px-4 py-3 bg-ink-900 border border-ink-700">
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          <span className="w-2 h-2 bg-ink-500 rounded-full animate-pulse-soft" />
          <span className="w-2 h-2 bg-ink-500 rounded-full animate-pulse-soft" style={{ animationDelay: '0.2s' }} />
          <span className="w-2 h-2 bg-ink-500 rounded-full animate-pulse-soft" style={{ animationDelay: '0.4s' }} />
        </div>
        <span className="text-ink-400 text-sm">Checking the wiki…</span>
      </div>
    </div>
  );
}
